import { useCallback, useEffect, useState } from 'react';
import { Toast } from 'antd-mobile';
import i18n from '../../../i18n';
import { authApi } from '../../../api/auth';
import { referralApi } from '../../../api/referral';
import { MyWhatsAppAccountBinding, whatsappApi } from '../../../api/whatsapp';
import { ApiEnvelope } from '../../../api/types';
import { AuthUser, useAuthStore } from '../../../stores/auth.store';
import { ProfileState } from './types';

function readErrorMessage(error: { response?: { data?: { error?: { message?: string } } } }, fallback: string): string {
  return error.response?.data?.error?.message || fallback;
}

export function useProfileModel() {
  const storeUser = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const [state, setState] = useState<ProfileState>({
    loading: true,
    user: storeUser,
    totalPoints: 0,
    whatsappSummary: null,
  });

  const load = useCallback(async () => {
    setState((previous) => ({ ...previous, loading: true }));
    try {
      const response = await authApi.me() as { data: ApiEnvelope<AuthUser> };
      const user = response.data.data;
      let whatsappSummary: ProfileState['whatsappSummary'] = null;
      let totalPoints = 0;
      if (user.role === 'promoter') {
        const [accountsResponse, referralResponse] = await Promise.all([
          whatsappApi.getMyAccounts() as Promise<{ data: ApiEnvelope<MyWhatsAppAccountBinding[]> }>,
          referralApi.getMyOverview() as Promise<{ data: ApiEnvelope<{ total_points: number }> }>,
        ]);
        const bindings = accountsResponse.data.data || [];
        whatsappSummary = {
          boundCount: bindings.length,
          onlineCount: bindings.filter((binding) => binding.connection_status === 'online').length,
        };
        totalPoints = referralResponse.data.data.total_points || 0;
      }
      setUser(user);
      setState({ loading: false, user, totalPoints, whatsappSummary });
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('profile:loadFailed')),
      });
      setState((previous) => ({ ...previous, loading: false }));
    }
  }, [setUser]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    state,
    reload: load,
  };
}
